/**
 * Vendor theme utilities — generate an accessible color palette from a single
 * brand hex and validate WCAG contrast. Uses Material Design 3's
 * `@material/material-color-utilities` if installed, otherwise falls back to a
 * simple HSL-shift algorithm so builds don't break before the dep lands.
 *
 * Output format: all colors are `#RRGGBB` hex strings. The runtime
 * PartnerVendorContext converts them to `H S% L%` triples for compatibility
 * with BHG's existing shadcn-style `hsl(var(--token))` CSS.
 */

export interface VendorThemeColors {
  color_primary: string;
  color_primary_foreground: string;
  color_secondary: string;
  color_secondary_foreground: string;
  color_accent: string;
  color_accent_foreground: string;
  color_background: string;
  color_foreground: string;
  color_muted: string;
  color_muted_foreground: string;
  color_card: string;
  color_card_foreground: string;
  color_border: string;
  color_ring: string;
}

// ---- Hex / HSL conversions --------------------------------------------------

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

/** Convert hex "#RRGGBB" → shadcn-compatible "H S% L%" string. */
export function hexToHslString(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
      case gn: h = (bn - rn) / d + 2; break;
      case bn: h = (rn - gn) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(1, s));
  l = Math.max(0, Math.min(1, l));
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r1 = 0, g1 = 0, b1 = 0;
  if (h < 60) { r1 = c; g1 = x; }
  else if (h < 120) { r1 = x; g1 = c; }
  else if (h < 180) { g1 = c; b1 = x; }
  else if (h < 240) { g1 = x; b1 = c; }
  else if (h < 300) { r1 = x; b1 = c; }
  else { r1 = c; b1 = x; }
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

function hexToHslObj(hex: string): { h: number; s: number; l: number } {
  const str = hexToHslString(hex);
  const [h, s, l] = str.split(/\s+/).map((part) => parseFloat(part));
  return { h, s: s / 100, l: l / 100 };
}

// ---- Contrast --------------------------------------------------------------

function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const [R, G, B] = [r, g, b].map((c) => {
    const cn = c / 255;
    return cn <= 0.03928 ? cn / 12.92 : Math.pow((cn + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

export function contrastRatio(fg: string, bg: string): number {
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const brightest = Math.max(l1, l2);
  const darkest = Math.min(l1, l2);
  return (brightest + 0.05) / (darkest + 0.05);
}

export function checkContrast(fg: string, bg: string): boolean {
  return contrastRatio(fg, bg) >= 4.5;
}

/** Pick `#FFFFFF` or `#09090B` as the foreground for a given background. */
function autoForeground(bgHex: string): string {
  return relativeLuminance(bgHex) > 0.5 ? "#09090B" : "#FFFFFF";
}

// ---- Theme generation -------------------------------------------------------

/**
 * Generate a 14-token palette from a single brand color.
 * Uses Material Color Utilities if available at runtime; otherwise a tuned
 * HSL-shift fallback. Output colors are hex — convert to HSL at the time
 * you write them to CSS variables.
 */
export function generateVendorTheme(
  brandHexColor: string,
  mode: "light" | "dark" = "light"
): VendorThemeColors {
  if (!/^#[0-9A-F]{6}$/i.test(brandHexColor)) {
    throw new Error("Invalid hex color. Expected format: #RRGGBB");
  }

  // Try Material Color Utilities dynamically (optional dep).
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
    const mcu = require("@material/material-color-utilities");
    if (mcu?.themeFromSourceColor && mcu?.argbFromHex && mcu?.hexFromArgb) {
      const sourceArgb = mcu.argbFromHex(brandHexColor);
      const theme = mcu.themeFromSourceColor(sourceArgb);
      const scheme = mode === "light" ? theme.schemes.light : theme.schemes.dark;
      const hex = (argb: number) => String(mcu.hexFromArgb(argb)).toUpperCase();
      return {
        color_primary: hex(scheme.primary),
        color_primary_foreground: hex(scheme.onPrimary),
        color_secondary: hex(scheme.secondaryContainer),
        color_secondary_foreground: hex(scheme.onSecondaryContainer),
        color_accent: hex(scheme.tertiaryContainer),
        color_accent_foreground: hex(scheme.onTertiaryContainer),
        color_background: hex(scheme.surface),
        color_foreground: hex(scheme.onSurface),
        color_muted: hex(scheme.surfaceContainerLow ?? scheme.surfaceVariant),
        color_muted_foreground: hex(scheme.onSurfaceVariant),
        color_card: hex(scheme.surface),
        color_card_foreground: hex(scheme.onSurface),
        color_border: hex(scheme.outlineVariant ?? scheme.outline),
        color_ring: hex(scheme.primary),
      };
    }
  } catch {
    /* fall through to HSL fallback */
  }

  return generateFallbackTheme(brandHexColor, mode);
}

/**
 * Deterministic HSL-shift fallback. Not as refined as Material Color Utilities,
 * but produces readable palettes without a heavy dependency.
 */
function generateFallbackTheme(brandHex: string, mode: "light" | "dark"): VendorThemeColors {
  const { h, s } = hexToHslObj(brandHex);
  const isDark = mode === "dark";

  const primary = brandHex;
  const primaryFg = autoForeground(primary);

  const toHex = (hh: number, ss: number, ll: number) => {
    const { r, g, b } = hslToRgb(hh, ss, ll);
    return rgbToHex(r, g, b);
  };

  const background = isDark ? toHex(h, Math.min(s, 0.2), 0.08) : "#FFFFFF";
  const foreground = isDark ? "#F4F4F5" : "#09090B";

  const secondaryL = isDark ? 0.18 : 0.94;
  const secondary = toHex(h, Math.min(s, 0.35), secondaryL);
  const secondaryFg = isDark ? "#F4F4F5" : toHex(h, Math.min(s, 0.55), 0.2);

  const accentL = isDark ? 0.22 : 0.92;
  const accent = toHex((h + 25) % 360, Math.min(s * 0.8, 0.35), accentL);
  const accentFg = isDark ? "#F4F4F5" : toHex((h + 25) % 360, Math.min(s, 0.55), 0.22);

  const mutedL = isDark ? 0.14 : 0.96;
  const muted = toHex(h, Math.min(s, 0.12), mutedL);
  const mutedFg = isDark ? "#A1A1AA" : toHex(h, Math.min(s, 0.25), 0.4);

  const card = isDark ? toHex(h, Math.min(s, 0.18), 0.11) : "#FFFFFF";
  const cardFg = isDark ? "#F4F4F5" : "#09090B";

  const borderL = isDark ? 0.2 : 0.9;
  const border = toHex(h, Math.min(s, 0.15), borderL);

  return {
    color_primary: primary,
    color_primary_foreground: primaryFg,
    color_secondary: secondary,
    color_secondary_foreground: secondaryFg,
    color_accent: accent,
    color_accent_foreground: accentFg,
    color_background: background,
    color_foreground: foreground,
    color_muted: muted,
    color_muted_foreground: mutedFg,
    color_card: card,
    color_card_foreground: cardFg,
    color_border: border,
    color_ring: primary,
  };
}

/**
 * Validate WCAG AA contrast on the five essential text/bg pairs.
 * Returns a report array suitable for showing inline in the theme editor.
 */
export function validateThemeContrast(theme: VendorThemeColors): Array<{
  pair: string;
  ratio: number;
  passes: boolean;
}> {
  const pairs: Array<{ name: string; fg: string; bg: string }> = [
    { name: "primary / primary-foreground", fg: theme.color_primary_foreground, bg: theme.color_primary },
    { name: "secondary / secondary-foreground", fg: theme.color_secondary_foreground, bg: theme.color_secondary },
    { name: "background / foreground", fg: theme.color_foreground, bg: theme.color_background },
    { name: "muted / muted-foreground", fg: theme.color_muted_foreground, bg: theme.color_muted },
    { name: "card / card-foreground", fg: theme.color_card_foreground, bg: theme.color_card },
  ];
  return pairs.map(({ name, fg, bg }) => {
    const ratio = contrastRatio(fg, bg);
    return { pair: name, ratio, passes: ratio >= 4.5 };
  });
}
